/**
 * crossFormResolver — loads shared field values from a related intake form.
 *
 * Primary use case: when rendering the sponsor's 40SP form, pre-load
 * shared relationship facts (marriage date, de facto start, etc.) from the
 * applicant's intake_form record. These fields are declared with
 * owner_form set to the applicant's form and shared_with including 40SP.
 *
 * The resolved values are merged into the sponsor's form state as read-only
 * context (they can be overridden by the sponsor, but we don't lock them).
 *
 * Secondary use: when rendering an applicant form, load sponsor identity
 * (name, email) as pre-filled context.
 */

import { supabase } from '../supabase';
import type { FormState } from './formStateAdapter';
import type { FormSchema } from '../schema-renderer/types';
import { collectSharedFieldNames } from './sharedFields';

/**
 * Load cross-form context for a sponsor's form by reading the applicant's
 * intake_form rows for the same matter.
 *
 * Returns a FormState containing only the shared fields that were found.
 * Empty object if no applicant form exists yet.
 */
export async function loadApplicantContextForSponsor(
  matterId: string,
  sponsorSchema: FormSchema,
): Promise<FormState> {
  try {
    // The sponsor's JWT cannot read the applicant's intake rows (RLS scopes
    // clients to their OWN forms — correct: the applicant's intake holds
    // private answers). The narrow SECURITY DEFINER bridge returns exactly
    // the values this schema declares as shared (owner_form != form_id),
    // resolved server-side against both storage shapes (autosave blob first,
    // flat seed rows fill gaps). Its allowlist is pinned to
    // collectSharedFieldNames by sharedContextAllowlist.test.ts.
    if (collectSharedFieldNames(sponsorSchema).length === 0) return {};

    const { data, error } = await (
      supabase.rpc as (
        fn: string,
        args: Record<string, unknown>,
      ) => PromiseLike<{ data: unknown; error: { message: string } | null }>
    )('get_applicant_shared_context', {
      p_matter_id: matterId,
      p_target_form: sponsorSchema.form_id,
    });

    if (error || !data || typeof data !== 'object' || Array.isArray(data)) {
      if (error) console.warn('crossFormResolver: shared-context rpc failed', error.message);
      return {};
    }
    return data as FormState;
  } catch (err) {
    console.warn('crossFormResolver: failed to load applicant context', err);
    return {};
  }
}

/**
 * Load sponsor identity context for an applicant's form.
 * Reads the sponsor_contact from the matter and returns pre-fill values
 * under the canonical names used in the applicant's schema.
 *
 * Returns empty object if no sponsor contact is on the matter.
 */
export async function loadSponsorContextForApplicant(
  matterId: string,
  visaSubclass: string,
): Promise<FormState> {
  try {
    const { data: matter, error: matterErr } = await supabase
      .from('matters')
      .select(
        'id, sponsor_contact:contacts!matters_sponsor_contact_id_fkey(given_names, family_name, email, phone)',
      )
      .eq('id', matterId)
      .maybeSingle();

    if (matterErr || !matter) return {};

    const sp = (
      matter as {
        sponsor_contact: {
          given_names: string | null;
          family_name: string | null;
          email: string | null;
          phone: string | null;
        } | null;
      }
    ).sponsor_contact;
    if (!sp) return {};

    const state: FormState = {};
    const push = (key: string, value: string | null | undefined) => {
      if (value && value.trim().length > 0) state[key] = value;
    };

    // Partner visas (820/801/309/100/300) no longer prefill the sponsor's identity
    // into the applicant's form. Privacy model (matches v1): the applicant must not
    // enter or own the sponsor's details; they see the sponsor's name read-only and
    // live instead. Only the visa types below still reference the sponsor by name on
    // the applicant side.
    if (visaSubclass === '461') {
      const fullName = [sp.given_names, sp.family_name].filter(Boolean).join(' ').trim();
      if (fullName) push('nzFamily.nzCitizenName', fullName);
    }
    if (visaSubclass === '600-family') {
      // v3 canonicals rendered by 1418_family_sponsored's sponsor_details section.
      push('sponsor.name.given_names', sp.given_names);
      push('sponsor.name.family_name', sp.family_name);
      push('sponsor.email', sp.email);
      push('sponsor.phone_mobile', sp.phone);
    }

    return state;
  } catch (err) {
    console.warn('crossFormResolver: failed to load sponsor context', err);
    return {};
  }
}
